/**
 * Copilot service functionality for handling AI responses
 *
 * This module provides utilities for interacting with the Copilot API,
 * processing streaming responses, and handling various response types including
 * text, data views, session updates, and action notifications.
 *
 * @module CopilotService
 * @example
 * import { getCopilotStreamAnswer, FEATURES, MODALITIES } from './copilot';
 *
 * getCopilotStreamAnswer({
 *   session: {
 *     features: [FEATURES.REPORTS],
 *     modalities: [MODALITIES.TEXT]
 *   },
 *   interactions: [{
 *     item: {
 *       content: { text: 'Hello, how can you help me?' }
 *     }
 *   }],
 *   setText: ({ text }) => console.log(text),
 *   onFinish: ({ text }) => console.log('Complete:', text)
 * });
 */
import { v4 as uuid } from 'uuid';
import { Fetch } from './sFetch';
import { CHART_REGEX, TABLE_REGEX } from 'shards/Copilot/hooks/constants';
import { getRegexMatch } from 'shards/Copilot/hooks/utils';
import {
    MODALITIES,
    FEATURES,
    FEATURES_CONFIG,
    SESSION_TYPES,
    INTERACTION_TYPES,
    INTERACTION_ITEM_TYPES,
    RESPONSE_TYPE,
    END_RESPONSE,
    COPILOT_FUNCTION_NAMES,
} from '../copilotConstants';

// Re-export constants for backward compatibility
export {
    MODALITIES,
    FEATURES,
    FEATURES_CONFIG,
    SESSION_TYPES,
    INTERACTION_TYPES,
    INTERACTION_ITEM_TYPES,
    RESPONSE_TYPE,
    END_RESPONSE,
    COPILOT_FUNCTION_NAMES,
};

/**
 * Processes a streaming response from the Copilot API
 *
 * @param {Object} options - Stream processing options
 * @param {Function} options.resolve - Promise resolution function
 * @param {Response} options.response - Fetch response object with body stream
 * @param {Function} [options.setSession] - Callback for session updates, receives session object
 * @param {Function} options.setText - Callback for text updates, receives {text, responseId}
 * @param {Function} [options.setData] - Callback for data/view updates, receives {target, view}
 * @param {Function} [options.onFinish] - Callback when stream is complete, receives {text}
 * @param {Function} [options.onError] - Callback for error handling
 * @param {Function} [options.onAction] - Callback for action completion, receives action object
 * @param {Function} [options.onFunction] - Async callback for function execution, receives function object, must return function result
 * @param {Function} [options.onSetReader] - Callback to receive the stream reader instance
 * @param {Function} [options.onFormProgress] - Callback for form progress updates, receives form object
 * @param {Function} [options.onToolInit] - Callback for tool initialization events, receives tool object
 * @param {Function} [options.onToolDone] - Callback for tool completion events, receives tool object
 * @returns {Promise} Promise that resolves when stream is processed
 */
const readStream = ({
    resolve,
    response,
    setSession,
    setText,
    setData,
    onFinish,
    onError,
    onAction,
    onArtifact,
    onSetReader,
    onFunction,
    onForm,
    onFormProgress,
    onToolInit,
    onToolDone,
    onTextDone,
}) => {
    const reader = response.body.getReader();
    onSetReader?.(reader);
    const decoder = new TextDecoder('utf-8');

    let text = '';
    let completedText = '';
    let isCanceled = false;
    let functionCalls = []; // Accumulate function calls from the stream

    /**
     * Processes text chunks from the stream
     *
     * @param {Object} chunk - Chunk data from the reader
     * @param {boolean} chunk.done - Whether the stream is complete
     * @param {Uint8Array} chunk.value - Binary data from the stream
     */
    const processText = async ({ done, value }) => {
        if (done) return;

        try {
            const chunk = decoder.decode(value, { stream: true });

            // Split and filter in one pass
            const lines = chunk
                .split('\n\n')
                .filter((line) => line.trim())
                .map((preLine) => {
                    const obj = {};

                    preLine.split('\n').forEach((current) => {
                        if (current.includes(`data: ${END_RESPONSE}`)) {
                            obj.content = END_RESPONSE;
                        } else if (current.startsWith('data:')) {
                            try {
                                Object.assign(obj, JSON.parse(current.substring(6)));
                            } catch {
                                // Invalid JSON - ignore this line
                            }
                        } else if (current.startsWith('event:')) {
                            obj.event = current.substring(6).trim();
                        } else if (current.startsWith('id:')) {
                            obj.index = current.substring(4).trim();
                        }
                    });

                    return obj;
                });

            for (const line of lines) {
                if (isCanceled) return resolve();

                if (line.content === END_RESPONSE) {
                    // Check if we have accumulated function calls to execute
                    if (functionCalls.length > 0 && onFunction) {
                        return resolve({
                            type: 'function_calls',
                            functionCalls,
                            text: completedText + text,
                        });
                    }

                    onFinish?.({ text });
                    return resolve(completedText + text);
                }

                if (!line.type) continue;

                switch (line.type) {
                    case RESPONSE_TYPE.ERROR_TYPE:
                        onError?.(line.message);
                        return resolve();
                    case RESPONSE_TYPE.SESSION_UPDATED:
                        setSession?.(line.session);
                        break;
                    case RESPONSE_TYPE.ARTIFACT_CREATED:
                        const artifact = line.artifact;
                        if (artifact?.type) {
                            const { type, id, params } = artifact;
                            switch (type) {
                                case 'generate_chart': {
                                    const chartId = getRegexMatch(id, CHART_REGEX);
                                    onArtifact?.({
                                        type: 'report',
                                        payload: { [chartId]: params },
                                    });
                                    break;
                                }
                                case 'generate_table': {
                                    const tableId = getRegexMatch(id, TABLE_REGEX);
                                    onArtifact?.({ type: 'table', payload: { [tableId]: params } });
                                    break;
                                }
                            }
                        }
                        break;
                    case RESPONSE_TYPE.ACTION_DONE:
                        onAction?.(line.action);
                        break;
                    case RESPONSE_TYPE.FORM_DONE:
                        onForm?.(line.form);
                        break;
                    case RESPONSE_TYPE.FUNCTION_CALL:
                        // Accumulate function calls to execute after stream completes
                        if (onFunction && line.function) {
                            functionCalls.push(line.function);
                        }
                        break;
                    case RESPONSE_TYPE.FORM_PROGRESS_TYPE:
                        onFormProgress?.(line.form);
                        break;
                    case RESPONSE_TYPE.TOOL_INIT_TYPE:
                        onToolInit?.(line.tool);
                        break;
                    case RESPONSE_TYPE.TOOL_DONE_TYPE:
                        onToolDone?.(line.tool);
                        break;
                    case RESPONSE_TYPE.PROGRESS_TYPE:
                        const { part } = line;
                        if (part?.type === RESPONSE_TYPE.PROGRESS_SUB_TYPE) {
                            setText({
                                text: `*${part.text}*...`,
                                responseId: line?.response_id,
                                type: RESPONSE_TYPE.PROGRESS_TYPE,
                            });
                        }
                        break;
                    case RESPONSE_TYPE.DATA_TYPE: {
                        const { part } = line;
                        if (
                            part?.type === RESPONSE_TYPE.VIEW_SUB_TYPE &&
                            part?.view?.data?.length
                        ) {
                            const featureTarget = part.view?.view;
                            const view = part.view;

                            if (featureTarget && view) {
                                setData?.({ target: featureTarget, view });
                            }
                        }
                        break;
                    }
                    case RESPONSE_TYPE.TEXT_TYPE:
                        text += line.delta;
                        setText?.({
                            text,
                            delta: line.delta,
                            responseId: line?.response_id,
                            type: RESPONSE_TYPE.TEXT_TYPE,
                        });
                        break;
                    case RESPONSE_TYPE.TEXT_DONE_TYPE:
                        onTextDone?.({
                            text: line.text || text,
                            responseId: line?.response_id,
                        });
                        completedText += line.text || text;
                        text = '';
                        break;
                    default:
                        break;
                }
            }

            // Continue reading
            const nextChunk = await reader.read();
            return processText(nextChunk);
        } catch (err) {
            console.error('Stream processing error:', err);
            onError?.();
            resolve();
        }
    };

    return reader.read().then(processText);
};

/**
 * Makes a request to the Copilot API and processes the streaming response
 *
 * @param {Object} options - Request options
 * @param {Object} [options.session={}] - Session configuration object
 * @param {string} [options.session.event_id] - Unique event identifier for the session
 * @param {string} [options.session.type] - Session type (defaults to SESSION_TYPES.CREATE)
 * @param {string} [options.session.id] - Session ID for existing sessions
 * @param {Array<string>} [options.session.modalities] - Session-specific modalities (defaults to [MODALITIES.TEXT])
 * @param {Array<string>} [options.session.features] - Session-specific features (defaults to [FEATURES.REPORTS])
 * @param {Array<Object>} [options.interactions=[]] - Array of interaction objects
 * @param {string} [options.interactions[].event_id] - Unique event identifier for the interaction
 * @param {string} [options.interactions[].type] - Interaction type (defaults to 'conversation.item.create')
 * @param {Object} [options.interactions[].item] - Interaction item details
 * @param {string} [options.interactions[].item.id] - Unique item identifier
 * @param {string} [options.interactions[].item.type] - Item type (defaults to INTERACTION_TYPES.MESSAGE)
 * @param {string} [options.interactions[].item.role] - Role of the interaction (defaults to 'user')
 * @param {Object} [options.interactions[].item.content] - Content of the interaction
 * @param {Function} [options.setSession] - Callback for session updates, receives session object
 * @param {Function} options.setText - Required callback for text updates, receives {text, responseId}
 * @param {Function} [options.setData] - Callback for data/view updates, receives {target, view}
 * @param {Function} [options.onFinish] - Callback when response is complete, receives {text}
 * @param {Function} [options.onError] - Callback for error handling
 * @param {Function} [options.onAction] - Callback for action completion, receives action object
 * @param {Function} [options.onFunction] - Async callback for function execution. When provided and function calls are received, promise resolves with {type: 'function_calls', functionCalls: [...], text}
 * @param {Function} [options.onSetReader] - Callback to receive the stream reader instance
 * @param {Function} [options.onFormProgress] - Callback for form progress updates, receives form object
 * @param {Function} [options.onToolInit] - Callback for tool initialization events, receives tool object
 * @param {Function} [options.onToolDone] - Callback for tool completion events, receives tool object
 * @returns {Promise<string|Object|void>} Promise resolving to response text, or {type: 'function_calls', functionCalls: [...], text} when function calls are received, or void on error/cancellation
 * @example
 * // Basic usage
 * getCopilotStreamAnswer({
 *   interactions: [{
 *     item: {
 *       content: { text: 'Generate a report for sales data' }
 *     }
 *   }],
 *   setText: ({ text, responseId }) => {
 *     console.log('Streaming text:', text);
 *   },
 *   onFinish: ({ text }) => {
 *     console.log('Final response:', text);
 *   }
 * });
 *
 * @example
 * // Advanced usage with session and features
 * getCopilotStreamAnswer({
 *   session: {
 *     id: 'existing-session-123',
 *     type: SESSION_TYPES.UPDATE,
 *     features: [FEATURES.REPORTS, FEATURES.KNOWLEDGE_BASE],
 *     modalities: [MODALITIES.TEXT, MODALITIES.VIEW]
 *   },
 *   interactions: [{
 *     item: {
 *       role: 'user',
 *       content: { text: 'Analyze the Q4 performance metrics' }
 *     }
 *   }],
 *   setSession: (session) => console.log('Session updated:', session),
 *   setText: ({ text }) => updateUI(text),
 *   setData: ({ target, view }) => renderDataView(target, view),
 *   onAction: (action) => handleAction(action),
 *   onError: () => showErrorMessage(),
 *   onSetReader: (reader) => setStreamReader(reader)
 * });
 *
 * @example
 * // Function calling pattern - execute functions and continue conversation
 * const result = await getCopilotStreamAnswer({
 *   session: { features: [FEATURES.SAGE_COPILOT_CRUD] },
 *   interactions: [{ item: { content: { text: 'Get the account form' } } }],
 *   setText: ({ text }) => console.log(text),
 *   onFunction: () => true // Just signal that function calls should be intercepted
 * });
 *
 * // If function calls were received, result will be:
 * // { type: 'function_calls', functionCalls: [{ name: '...', arguments: '...', callId: '...' }, ...], text: '...' }
 * // Execute all functions and send results back:
 * if (result?.type === 'function_calls') {
 *   const functionResults = await Promise.all(
 *     result.functionCalls.map(async (fn) => {
 *       const args = JSON.parse(fn.arguments);
 *       const output = await executeFunction(fn.name, args);
 *       return {
 *         item: {
 *           type: INTERACTION_TYPES.FUNCTION_CALL_OUTPUT,
 *           content: {
 *             type: INTERACTION_TYPES.FUNCTION_CALL_OUTPUT,
 *             callId: fn.callId,
 *             output: JSON.stringify(output)
 *           }
 *         }
 *       };
 *     })
 *   );
 *
 *   await getCopilotStreamAnswer({
 *     session: { id: sessionId, type: SESSION_TYPES.UPDATE },
 *     interactions: functionResults,
 *     setText: ({ text }) => console.log(text),
 *     onFinish: ({ text }) => console.log('Done:', text)
 *   });
 * }
 */
export const getCopilotStreamAnswer = ({
    session = {},
    interactions = [],
    setSession,
    setText,
    setData,
    onFinish,
    onError,
    onAction,
    onFunction,
    onArtifact,
    onSetReader,
    onForm,
    onFormProgress,
    onToolInit,
    onToolDone,
    onTextDone,
}) => {
    return new Promise((resolve) => {
        if (!interactions.length) return resolve();

        const features = session.features?.length ? session.features : [FEATURES.REPORTS];

        const tools = features.reduce((acc, feature) => {
            return [...acc, ...(FEATURES_CONFIG[feature]?.tools || [])];
        }, []);

        // Build session object
        const finalSession = {
            event_id: session?.event_id || uuid(),
            type: session?.type || SESSION_TYPES.CREATE,
            session: {
                id: session.id || null,
                modalities: session.modalities?.length ? session.modalities : [MODALITIES.TEXT],
                features,
                tools,
                inputAudioFormat: session.inputAudioFormat || undefined,
            },
        };

        // Build interactions array of objects
        const finalInteractions = interactions.map((interaction) => {
            // Audio buffer interactions (append/commit) don't have an 'item' property
            // They should be passed through with audio, event_id, and type at the top level
            const isAudioBufferInteraction =
                interaction?.type === INTERACTION_ITEM_TYPES.INPUT_AUDIO_BUFFER_APPEND ||
                interaction?.type === INTERACTION_ITEM_TYPES.INPUT_AUDIO_BUFFER_COMMIT;

            if (isAudioBufferInteraction) {
                return {
                    event_id: interaction?.event_id || uuid(),
                    type: interaction.type,
                    ...(interaction?.audio && { audio: interaction.audio }),
                };
            }

            // Standard interactions with 'item' property
            return {
                event_id: interaction?.event_id || uuid(),
                type: interaction?.type || 'conversation.item.create',
                item: {
                    id: interaction?.item?.id || uuid(),
                    type: interaction?.item?.type || INTERACTION_TYPES.MESSAGE,
                    role: interaction?.item?.role || 'user',
                    content: interaction?.item?.content || {},
                },
            };
        });

        // Standard response creation object
        const extra = {
            event_id: uuid(),
            type: 'response.create',
        };

        const body = [finalSession, ...finalInteractions, extra];

        Fetch('/analyzer/v1/copilot?format=web', {
            method: 'POST',
            headers: {
                Accept: 'text/event-stream',
            },
            body: JSON.stringify(body),
        })
            .then((response) => {
                if (response.status !== 200) {
                    onError?.();
                    resolve();
                } else {
                    readStream({
                        resolve,
                        response,
                        setSession,
                        setText,
                        setData,
                        onFinish,
                        onError,
                        onAction,
                        onArtifact,
                        onSetReader,
                        onFunction,
                        onForm,
                        onFormProgress,
                        onToolInit,
                        onToolDone,
                        onTextDone,
                    });
                }
            })
            .catch((err) => {
                console.error('Copilot stream request error:', err);
                onError?.();
                resolve();
            });
    });
};

/**
 * Handles a complete Copilot conversation with automatic function calling
 *
 * This function manages the full conversation flow including:
 * 1. Initial user message
 * 2. Function execution when requested by AI
 * 3. Sending function results back
 * 4. Continuing conversation until completion
 *
 * @param {Object} options - Conversation options
 * @param {Object} options.session - Session configuration
 * @param {Array<Object>} options.interactions - Initial interactions
 * @param {Object} options.functionRegistry - Map of function names to implementations
 * @param {Function} options.setText - Callback for text updates
 * @param {Function} [options.setSession] - Callback for session updates
 * @param {Function} [options.setData] - Callback for data updates
 * @param {Function} [options.onFinish] - Callback when conversation completes
 * @param {Function} [options.onError] - Callback for errors
 * @param {Function} [options.onAction] - Callback for actions
 * @param {Function} [options.onArtifact] - Callback for artifacts
 * @param {Function} [options.onSetReader] - Callback for stream reader
 * @param {Function} [options.onFormProgress] - Callback for form progress updates, receives form object
 * @param {Function} [options.onToolInit] - Callback for tool initialization events, receives tool object
 * @param {Function} [options.onToolDone] - Callback for tool completion events, receives tool object
 * @param {number} [options.maxFunctionCalls=10] - Maximum function calls to prevent infinite loops
 * @returns {Promise<string|void>} Final response text or void on error
 *
 * @example
 * // Complete function calling example
 * const functionRegistry = {
 *   get_initial_form: ({ entity }) => {
 *     return fetch(`/api/forms/${entity}`)
 *       .then(response => response.json());
 *   },
 *   create_entity: ({ entity, data }) => {
 *     return fetch(`/api/${entity}`, {
 *       method: 'POST',
 *       body: JSON.stringify(data)
 *     }).then(response => response.json());
 *   }
 * };
 *
 * getCopilotStreamAnswerWithFunctions({
 *   session: {
 *     features: [FEATURES.SAGE_COPILOT_CRUD]
 *   },
 *   interactions: [{
 *     item: {
 *       content: { text: 'Create a new account named Acme Corp' }
 *     }
 *   }],
 *   functionRegistry,
 *   setText: ({ text }) => updateUI(text),
 *   onFinish: ({ text }) => console.log('Done:', text)
 * });
 */
export const getCopilotStreamAnswerWithFunctions = ({
    session = {},
    interactions = [],
    functionRegistry = {},
    setText,
    setSession,
    setData,
    onFinish,
    onError,
    onAction,
    onArtifact,
    onSetReader,
    onForm,
    onFormProgress,
    onToolInit,
    onToolDone,
    onTextDone,
    maxFunctionCalls = 10,
}) => {
    let currentSession = session;
    let currentInteractions = interactions;
    let functionCallCount = 0;
    let finalText = '';

    // Recursive function to handle conversation with potential function calls
    const processConversation = () => {
        if (functionCallCount >= maxFunctionCalls) {
            console.error('Max function calls reached, stopping to prevent infinite loop');
            onError?.();
            return Promise.resolve();
        }

        return getCopilotStreamAnswer({
            session: currentSession,
            interactions: currentInteractions,
            setText,
            setSession: (session) => {
                currentSession = {
                    ...currentSession,
                    id: session.id,
                    type: SESSION_TYPES.UPDATE,
                };
                setSession?.(session);
            },
            setData,
            onFinish,
            onError,
            onAction,
            onArtifact,
            onSetReader,
            onForm,
            onFormProgress,
            onToolInit,
            onToolDone,
            onTextDone,
            onFunction: () => true, // Signal that function calls should be intercepted
        }).then(async (result) => {
            // Check if we got function calls that need to be executed
            if (result?.type === 'function_calls') {
                finalText = result.text;
                functionCallCount += result.functionCalls.length;

                // Execute all functions in parallel
                const functionResultsPromises = result.functionCalls.map(async (fn) => {
                    const { name, arguments: args, callId } = fn;

                    try {
                        const argsObj = JSON.parse(args);

                        // Check if function exists in registry
                        if (!functionRegistry[name]) {
                            console.error(`Function ${name} not found in registry`);
                            return {
                                callId,
                                output: JSON.stringify({
                                    error: `Function ${name} is not available`,
                                }),
                            };
                        }

                        // Execute the function and return result
                        const functionResult = await Promise.resolve(
                            functionRegistry[name](argsObj),
                        );

                        return {
                            callId,
                            output: JSON.stringify(functionResult),
                        };
                    } catch (error) {
                        console.error(`Function ${name} execution failed:`, error);
                        return {
                            callId,
                            output: JSON.stringify({
                                error: error.message || 'Function execution failed',
                            }),
                        };
                    }
                });

                // Wait for all functions to complete
                const functionResults = await Promise.all(functionResultsPromises);

                // Prepare interactions with all function results
                currentInteractions = functionResults.map((fnResult) => ({
                    item: {
                        id: uuid(),
                        type: INTERACTION_TYPES.FUNCTION_CALL_OUTPUT,
                        content: {
                            type: INTERACTION_TYPES.FUNCTION_CALL_OUTPUT,
                            callId: fnResult.callId,
                            output: fnResult.output,
                        },
                    },
                }));

                // Continue the conversation with all function results
                return processConversation();
            }

            // Conversation complete — readStream already called onFinish with
            // legacy semantics (trailing delta only); do not call it again here.
            if (result !== undefined) {
                finalText = result || finalText;
            }
            return finalText;
        });
    };

    return processConversation();
};
